import { defineMessages, useIntl } from 'react-intl';

import { BodyText } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const AddToBagUnavailableIngredientsBody = (props: Props) => {
  const { count, productName, locationName, isReplacementRequired } = props;

  const { formatMessage } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  const message = getBodyMessage({ count, isReplacementRequired });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <BodyText sizeMatch={['24']}>
      {formatMessage(message, { location_name: locationName, product_name: productName })}
    </BodyText>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getBodyMessage(params: GetBodyMessageParams) {
  const { count, isReplacementRequired } = params;

  const hasSingleIngredient = count === 1;

  if (isReplacementRequired) {
    return hasSingleIngredient
      ? messages.singleIngredientReplacementRequired
      : messages.multipleIngredientsReplacementRequired;
  }

  return hasSingleIngredient ? messages.singleIngredient : messages.multipleIngredients;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  singleIngredient: {
    defaultMessage:
      'This item is usually in the {product_name}, but {location_name} is currently out of stock.',
    description: 'Add To Bag | Unavailable ingredients | Body (single ingredient)',
  },
  multipleIngredients: {
    defaultMessage:
      'These items are usually in the {product_name}, but {location_name} is currently out of stock.',
    description: 'Add To Bag | Unavailable ingredients | Body (multiple ingredients)',
  },
  singleIngredientReplacementRequired: {
    defaultMessage:
      'This item is usually in the {product_name}, but {location_name} is currently out of stock. Continue to choose a new ingredient.',
    description:
      'Add To Bag | Unavailable ingredients | Body when a replacement is required (single ingredient)',
  },
  multipleIngredientsReplacementRequired: {
    defaultMessage:
      'These items are usually in the {product_name}, but {location_name} is currently out of stock. Continue to choose new ingredients.',
    description:
      'Add To Bag | Unavailable ingredients | Body when a replacement is required (multiple ingredients)',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  count: number;
  productName: string;
  locationName: string;
  isReplacementRequired: boolean;
};

type GetBodyMessageParams = Pick<Props, 'count' | 'isReplacementRequired'>;
